<template>
  <div class="configuration-page pa-6 pa-md-10">
    <v-card class="mb-4">
      <v-card-text class="pb-0">
        <div class="text-h6">{{ $t('charging-point-fleet-configuration.title') }}</div>
        <div class="text-caption text--secondary">{{ $t('charging-point-fleet-configuration.explained') }}</div>
      </v-card-text>
      <v-tabs v-model="tab">
        <v-tab>{{ $t('charging-point-fleet-configuration.tabs.tracked') }}</v-tab>
        <v-tab>{{ $t('charging-point-fleet-configuration.tabs.search') }}</v-tab>
        <v-tab>{{ $t('charging-point-fleet-configuration.tabs.changes') }}</v-tab>
        <v-tab>{{ $t('charging-point-fleet-configuration.tabs.batches') }}</v-tab>
      </v-tabs>
    </v-card>

    <v-alert v-if="!allowed" type="warning" text dense>{{ $t('charging-point-fleet-configuration.operator-only') }}</v-alert>
    <charging-point-configuration-batches v-else-if="tab === 3" :open-id="$route.query.batch || null" />
    <template v-else>
      <v-card outlined class="mb-4">
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="8">
              <charging-point-classification-select v-model="classification" />
            </v-col>
            <v-col cols="12" md="4">
              <organization-select v-model="organizationId" :label="$t('charging-point-fleet-configuration.organization')" clearable />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <tracked-keys v-if="tab === 0" :filters="filters" @batch-created="followBatch" />

      <!-- Chercher une clé brute, telle que la borne la nomme : la répartition de ses valeurs, les bornes d'une valeur -->
      <template v-else-if="tab === 1">
        <v-card outlined class="mb-4">
          <v-card-text>
            <v-autocomplete
                v-model="key"
                :items="keys ? keys.keys : []"
                :loading="loadingKeys"
                :label="$t('charging-point-fleet-configuration.key')"
                item-text="key"
                item-value="key"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
                class="mono"
            >
              <template #item="{item}">
                <v-list-item-content><v-list-item-title class="mono">{{ item.key }}</v-list-item-title></v-list-item-content>
                <v-list-item-action-text>{{ item.chargingPoints }}</v-list-item-action-text>
              </template>
            </v-autocomplete>
            <div v-if="keys" class="text-caption text--secondary mt-2">{{ $t('charging-point-fleet-configuration.scope', {read: keys.read, total: keys.chargingPoints}) }}</div>
          </v-card-text>
        </v-card>

        <v-card v-if="key" outlined class="mb-4">
          <v-card-title class="text-subtitle-1 mono">{{ key }}</v-card-title>
          <v-card-text>
            <div v-if="!values" class="text-center my-4"><v-progress-circular indeterminate color="primary" /></div>
            <template v-else>
              <v-alert v-if="values.sensitive" type="info" text dense>{{ $t('charging-point-fleet-configuration.sensitive') }}</v-alert>
              <v-simple-table dense>
                <tbody>
                  <tr v-for="row in valueRows" :key="row.id" :class="{selected: selected && selected.id === row.id}" class="clickable" @click="select(row)">
                    <td class="mono" style="width: 40%">
                      <span v-if="row.missing" class="text--secondary">{{ $t('charging-point-fleet-configuration.missing') }}</span>
                      <span v-else-if="row.value === null" class="text--secondary">{{ values.sensitive ? $t('charging-point-fleet-configuration.hidden') : '∅' }}</span>
                      <template v-else-if="row.values">
                        <span>{{ $t('charging-point-tracked-keys.truth.' + row.value) }}</span>
                        <div class="d-flex flex-wrap mt-1" style="gap: 4px">
                          <v-chip v-for="spelling in row.spellings" :key="spelling.value" x-small outlined :input-value="selected && selected.id === spelling.id" @click.stop="select(spelling)">
                            {{ spelling.value }} · {{ spelling.chargingPoints }}
                          </v-chip>
                        </div>
                      </template>
                      <span v-else>{{ row.value === '' ? '""' : row.value }}</span>
                    </td>
                    <td>
                      <v-progress-linear :value="values.read ? row.chargingPoints * 100 / values.read : 0" height="8" rounded :color="row.missing ? 'grey' : 'primary'" />
                    </td>
                    <td class="text-right text-no-wrap" style="width: 1%">{{ $tc('charging-point-fleet-configuration.charging-points', row.chargingPoints, {count: row.chargingPoints}) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </template>
          </v-card-text>
        </v-card>

        <v-card v-if="selected" outlined>
          <v-card-title class="text-subtitle-1">
            {{ $tc('charging-point-fleet-configuration.charging-points', list ? list.total : 0, {count: list ? list.total : 0}) }}
            <span class="mono ml-2">
              {{ key }} = <template v-if="selected.missing">{{ $t('charging-point-fleet-configuration.missing') }}</template><template v-else-if="selected.values">{{ selected.values.join(' / ') }}</template><template v-else>{{ selected.value === null ? '∅' : selected.value }}</template>
            </span>
          </v-card-title>
          <v-data-table
              :headers="chargingPointHeaders"
              :items="list ? list.rows : []"
              :loading="loadingList"
              :server-items-length="list ? list.total : 0"
              :options.sync="listOptions"
              :footer-props="{'items-per-page-options': [50, 100, 200]}"
              dense
          >
            <template #item.name="{item}">
              <router-link :to="{name: 'charging-points.detail.manage', params: {id: item.chargingPointId}}">{{ item.name || item.identifier || item.chargingPointId }}</router-link>
              <v-icon x-small :color="item.connected ? 'success' : 'grey'" class="ml-1">mdi-circle</v-icon>
            </template>
            <template #item.type="{item}">{{ [item.manufacturerName, item.productLineName].filter(v => v).join(' · ') }}</template>
            <template #item.since="{item}">{{ item.since ? formatDate(item.since, 'DD/MM/YYYY') : '' }}</template>
          </v-data-table>
        </v-card>
      </template>

      <!-- Les changements constatés : hors supervision par défaut, ou faits par nous, ou par un firmware -->
      <charging-point-configuration-changes v-else :filters="filters" />
    </template>
  </div>
</template>

<script>
import ChargingPointConfigurationRepository from "@repository/ChargingPointConfigurationRepository";
import ChargingPointConfigurationValue from "./ChargingPointConfigurationValue";
import ChargingPointClassificationSelect from "@blocks/Select/ChargingPointClassificationSelect.vue";
import OrganizationSelect from "@blocks/Select/OrganizationSelect.vue";
import TrackedKeys from "./TrackedKeys.vue";
import ChargingPointConfigurationBatches from "./ChargingPointConfigurationBatches.vue";
import ChargingPointConfigurationChanges from "./ChargingPointConfigurationChanges.vue";

/** Les onglets, gardés dans l'adresse (`?tab=`) : un lien mène droit au suivi d'une modification en masse. */
const TABS = ['tracked', 'search', 'changes', 'batches']

/**
 * La configuration OCPP du parc (ZEborne seul) : les clés suivies (répartition toutes marques, écarts, modification en
 * masse), la recherche par clé brute telle que la borne la nomme, les changements constatés (hors supervision, par nous,
 * par un firmware), et le suivi des modifications en masse. Filtrable par Constructeur › Gamme › Type et organisation.
 */
export default {
  components: {ChargingPointClassificationSelect, OrganizationSelect, TrackedKeys, ChargingPointConfigurationBatches, ChargingPointConfigurationChanges},

  data() {
    return {
      classification: {manufacturerId: null, productLineId: null, chargingPointTypeId: null},
      organizationId: null,
      keys: null,
      loadingKeys: false,
      key: null,
      values: null,
      selected: null,
      list: null,
      loadingList: false,
      listOptions: {page: 1, itemsPerPage: 50},
    }
  },

  computed: {
    tab: {
      get() {
        return Math.max(0, TABS.indexOf(this.$route.query.tab))
      },
      set(index) {
        if (index !== this.tab) {
          this.$router.replace({query: {...this.$route.query, tab: TABS[index], batch: undefined}})
        }
      },
    },
    allowed() {
      return !!this.currentUser?.rootOrganizationId && (this.currentUser?.permissions || []).includes('STATION_CONFIGURATION')
    },
    filters() {
      return {...this.classification, organizationId: this.organizationId}
    },
    // Un booléen : regroupé par sens, chaque écriture dessous (comme dans la fenêtre d'une clé).
    valueRows() {
      let rows = this.values.values.map((row, index) => ({id: 'v' + index, value: row.value, chargingPoints: row.chargingPoints, missing: false}))
      if (!this.values.sensitive && ChargingPointConfigurationValue.isBoolean(rows.map(row => row.value))) {
        rows = [true, false].map(truth => {
          const spellings = rows.filter(row => ChargingPointConfigurationValue.truth(row.value) === truth)
          return {id: 'truth-' + truth, value: String(truth), values: spellings.map(row => row.value), spellings, chargingPoints: spellings.reduce((sum, row) => sum + row.chargingPoints, 0), missing: false}
        }).filter(row => row.spellings.length)
      }
      const missing = this.values.read - this.values.withKey
      if (missing > 0) {
        rows.push({id: 'missing', value: null, chargingPoints: missing, missing: true})
      }
      return rows
    },
    chargingPointHeaders() {
      return [
        {text: this.$t('charging-point-fleet-configuration.charging-point'), value: 'name', sortable: false},
        {text: this.$t('charging-point-fleet-configuration.organization'), value: 'organizationName', sortable: false},
        {text: this.$t('charging-point-fleet-configuration.location'), value: 'locationName', sortable: false},
        {text: this.$t('classification.manufacturer') + ' · ' + this.$t('classification.product-line'), value: 'type', sortable: false},
        {text: this.$t('charging-point-fleet-configuration.since'), value: 'since', sortable: false},
      ]
    },
  },

  watch: {
    // Les clés brutes du parc (une agrégation lourde) seulement sur l'onglet Recherche.
    filters() {
      this.keys = null
      if (this.tab === 1) {
        this.loadKeys()
        this.loadValues()
      }
    },
    key() {
      this.loadValues()
    },
    tab(tab) {
      if (tab === 1 && this.keys === null) {
        this.loadKeys()
      }
    },
    listOptions: {
      handler() {
        this.loadList()
      },
      deep: true,
    },
  },

  mounted() {
    if (this.allowed && this.tab === 1) {
      this.loadKeys()
    }
  },

  methods: {
    loadKeys() {
      this.loadingKeys = true
      ChargingPointConfigurationRepository.search(this.filters).then(keys => {
        this.keys = keys
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loadingKeys = false
      })
    },

    loadValues() {
      this.values = null
      this.selected = null
      this.list = null
      if (!this.key) {
        return
      }
      const key = this.key
      ChargingPointConfigurationRepository.search(this.filters, key).then(values => {
        if (key === this.key) {
          this.values = values
        }
      }).catch(err => {
        this.notifyError(err)
      })
    },

    select(row) {
      this.selected = row
      this.list = null
      // Le changement de page (même à la première) relance la liste.
      this.listOptions = {...this.listOptions, page: 1}
    },

    loadList() {
      if (!this.selected) {
        return
      }
      const {page, itemsPerPage} = this.listOptions
      this.loadingList = true
      ChargingPointConfigurationRepository.chargingPoints(this.filters, this.key, {
        value: this.selected.missing ? null : this.selected.value,
        values: this.selected.values || null,
        missing: this.selected.missing,
        offset: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
      }).then(list => {
        this.list = list
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loadingList = false
      })
    },

    followBatch(batchId) {
      this.$router.replace({query: {...this.$route.query, tab: 'batches', batch: batchId}})
    },

    display(value) {
      return value === null || value === '' ? '∅' : value
    },
  },
}
</script>

<style scoped lang="scss">
.mono {
  font-family: monospace;
}
.clickable {
  cursor: pointer;
}
.selected {
  background: rgba(0, 0, 0, 0.05);
}
</style>
